<template>
  <div class="invoice-frequency-list-container invoice-frequency-list-main invoice-frequency-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Invoice Frequency List']" />

    <div class="glass-tab-view glass-tab-view--flush invoice-frequency-list-glass-shell">
      <div class="glass-tab-view__content invoice-frequency-list-tab-content invoice-frequency-list-page-stack">
        <div class="invoice-frequency-list-header-block">
          <div class="invoice-frequency-list-header-row">
            <div class="invoice-frequency-list-header-row__text">
              <h2>Invoice Frequencies</h2>
              <p class="invoice-frequency-list-header-row__sub">Create and manage billing frequency options for invoices</p>
            </div>
            <div class="invoice-frequency-list-header-actions">
              <PrimaryButton label="Add New" @click="showModal = true" />
            </div>
          </div>
        </div>

        <div class="invoice-frequency-list-glass-panel invoice-frequency-list-main-card">
          <div class="consultant-glass-table-section invoice-frequency-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table invoice-frequency-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th class="invoice-frequency-list-delete-header">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="2" class="consultant-glass-table-no-records invoice-frequency-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="2" class="consultant-glass-table-no-records invoice-frequency-list-no-records">{{ error }}</td>
                    </tr>
                    <tr v-else-if="frequencies.length === 0">
                      <td colspan="2" class="consultant-glass-table-no-records invoice-frequency-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(frequency, idx) in frequencies"
                        :key="frequency.id || idx"
                        class="consultant-glass-table-row invoice-frequency-list-row"
                        :style="{ '--ifl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td>{{ frequency.name }}</td>
                        <td class="invoice-frequency-list-delete-cell">
                          <SecondaryButton
                            label="Delete"
                            icon="bi bi-trash"
                            custom-class="consultant-secondary-btn--danger invoice-frequency-list-delete-btn"
                            @click="openDeleteModal(idx)"
                          />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddNewInvoiceFrequency
      :visible="showModal"
      :companyId="companyId"
      @ok="handleInvoiceFrequencyCreated"
      @cancel="closeModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      buttonText="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      buttonText="OK"
      @close="closeErrorModal"
    />

    <WarningModal
      :show="showWarningModal"
      title="Delete Invoice Frequency"
      :message="warningMessage"
      buttonText="Cancel"
      confirmButtonText="Delete"
      @close="closeWarningModal"
      @confirm="confirmDeleteFrequency"
    />
  </div>
</template>

<script>
import '../styles/InvoiceFrequencyList.css';
import AddNewInvoiceFrequency from './AddNewInvoiceFrequency.vue';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'InvoiceFrequencyList',
  components: {
    AddNewInvoiceFrequency,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      showModal: false,
      frequencies: [],
      loading: false,
      error: '',
      companyId: this.$route.params.id,
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      errorMessage: '',
      warningMessage: '',
      successMessage: '',
      deleteIdx: null,
      isDeleting: false,
    };
  },
  computed: {
    hasFrequencies() {
      return this.frequencies && this.frequencies.length > 0;
    },
    isLoading() {
      return this.loading || this.isDeleting;
    },
    canDelete() {
      return this.hasFrequencies && !this.isLoading;
    },
  },
  async mounted() {
    await this.fetchFrequencies();
  },
  methods: {
    async fetchFrequencies() {
      this.loading = true;
      this.error = '';
      try {
        this.frequencies = await CompanyService.getAllInvoiceFrequencies(this.companyId);
        logger.log('Successfully fetched invoice frequencies:', this.frequencies.length);
      } catch (err) {
        logger.error('Error fetching invoice frequencies:', err);

        if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Invoice frequencies service not found. Please contact support.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to view invoice frequencies.';
        } else if (err.response?.status === 500) {
          this.errorMessage =
            'Internal server error occurred while fetching invoice frequencies. The server encountered an unexpected condition. Please try again in a few minutes.';
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage =
            'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while fetching invoice frequencies. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = 'An unexpected error occurred while fetching invoice frequencies. Please try again.';
        }
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.showModal = false;
    },
    async handleInvoiceFrequencyCreated(createdFrequency) {
      this.closeModal();
      await this.fetchFrequencies();
      const frequencyCount = this.frequencies.length;
      this.successMessage = `Invoice frequency "${createdFrequency?.name || 'New Invoice Frequency'}" created successfully! You now have ${frequencyCount} invoice frequenc${frequencyCount !== 1 ? 'ies' : 'y'} in your system.`;
      this.showSuccessModal = true;
      logger.log('Invoice frequency created successfully, total frequencies:', frequencyCount);
    },
    openDeleteModal(idx) {
      const frequency = this.frequencies[idx];
      if (!frequency || !frequency.id) {
        this.errorMessage = 'Invalid frequency selected. Please refresh the page and try again.';
        this.showErrorModal = true;
        return;
      }

      if (!frequency.name || frequency.name.trim() === '') {
        this.errorMessage = 'Cannot delete a frequency with an empty name.';
        this.showErrorModal = true;
        return;
      }

      this.deleteIdx = idx;
      this.warningMessage = `Are you sure you want to delete the invoice frequency "${frequency.name}"? This action cannot be undone and may affect invoices assigned to this frequency.`;
      this.showWarningModal = true;
      logger.log('Delete confirmation requested for invoice frequency:', frequency.name);
    },

    closeWarningModal() {
      this.showWarningModal = false;
      this.deleteIdx = null;
      this.warningMessage = '';
    },

    async confirmDeleteFrequency() {
      if (this.deleteIdx !== null) {
        const frequency = this.frequencies[this.deleteIdx];
        if (!frequency || !frequency.id) return;

        this.isDeleting = true;
        this.showWarningModal = false;

        try {
          logger.log('Attempting to delete invoice frequency:', frequency.name, 'ID:', frequency.id);
          await CompanyService.deleteInvoiceFrequency(frequency.id);
          this.frequencies.splice(this.deleteIdx, 1);
          this.error = '';

          const remainingCount = this.frequencies.length;
          this.successMessage = `Invoice frequency "${frequency.name}" has been deleted successfully. You now have ${remainingCount} invoice frequenc${remainingCount !== 1 ? 'ies' : 'y'} remaining.`;
          this.showSuccessModal = true;
          logger.log('Invoice frequency deleted successfully, remaining frequencies:', remainingCount);
        } catch (err) {
          logger.error('Error deleting invoice frequency:', err);

          const errorText =
            err.response?.data?.message ||
            err.response?.data?.error ||
            err.response?.data?.detail ||
            err.message ||
            '';

          if (
            errorText.includes('foreign key constraint') ||
            errorText.includes('violates foreign key') ||
            errorText.includes('referenced by') ||
            errorText.includes('cannot delete') ||
            errorText.includes('constraint') ||
            errorText.includes('invoices') ||
            errorText.includes('invoiceFrequencyId') ||
            errorText.includes('InvoiceFrequencies')
          ) {
            if (
              errorText.includes('invoices') ||
              errorText.includes('invoiceFrequencyId') ||
              errorText.includes('InvoiceFrequencies') ||
              errorText.includes('invoice')
            ) {
              this.errorMessage = `Cannot delete "${frequency.name}" because it is currently assigned to one or more invoices. Please reassign those invoices to another frequency first.`;
            } else {
              this.errorMessage = `Cannot delete "${frequency.name}" because it is currently being used by other records. Please reassign those records to another frequency first.`;
            }
          } else if (err.response?.data?.message) {
            this.errorMessage = err.response.data.message;
          } else if (err.response?.data?.error) {
            this.errorMessage = err.response.data.error;
          } else if (err.response?.data?.detail) {
            this.errorMessage = err.response.data.detail;
          } else if (err.response?.status === 409) {
            this.errorMessage = `Cannot delete "${frequency.name}" because it is currently assigned to one or more invoices. Please reassign those invoices to another frequency first.`;
          } else if (err.response?.status === 404) {
            this.errorMessage = 'Invoice frequency not found. It may have already been deleted.';
          } else if (err.response?.status === 403) {
            this.errorMessage = 'You do not have permission to delete invoice frequencies.';
          } else if (err.response?.status === 500) {
            const serverErrorText =
              err.response?.data?.message || err.response?.data?.error || err.response?.data?.detail || '';
            if (
              serverErrorText.includes('foreign key constraint') ||
              serverErrorText.includes('violates foreign key') ||
              serverErrorText.includes('referenced by') ||
              serverErrorText.includes('invoices') ||
              serverErrorText.includes('invoiceFrequencyId') ||
              serverErrorText.includes('InvoiceFrequencies')
            ) {
              this.errorMessage = `Cannot delete "${frequency.name}" because it is currently assigned to one or more invoices. Please reassign those invoices to another frequency first.`;
            } else {
              this.errorMessage =
                'Internal server error occurred while deleting the invoice frequency. The server encountered an unexpected condition. Please try again in a few minutes.';
            }
          } else if (err.response?.status === 502) {
            this.errorMessage =
              'Bad gateway error. The server received an invalid response from another server. Please try again later.';
          } else if (err.response?.status === 503) {
            this.errorMessage =
              'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
          } else if (err.response?.status === 504) {
            this.errorMessage = 'Gateway timeout. The server did not receive a timely response. Please try again later.';
          } else if (err.response?.status >= 500) {
            this.errorMessage = `Server error (${err.response.status}) occurred while deleting the invoice frequency. Please try again later or contact support if the problem persists.`;
          } else if (err.code === 'NETWORK_ERROR' || !err.response) {
            this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
          } else if (err.message) {
            this.errorMessage = err.message;
          } else {
            this.errorMessage = `An unexpected error occurred while deleting "${frequency.name}". Please try again.`;
          }
          this.showErrorModal = true;
        } finally {
          this.isDeleting = false;
          this.deleteIdx = null;
          this.warningMessage = '';
        }
      }
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
    },

    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>
